import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  approveApprovalStep,
  downloadApprovalPdf,
  fetchApproval,
  rejectApprovalStep,
} from '../api/approval';
import {
  ApprovalRequest,
  ApprovalStep,
  approvalStatusLabel,
  approvalStepStatusLabel,
  approvalStepTypeLabel,
  approvalTargetLabel,
} from '../types/approval';
import ApprovalTargetPreview from '../components/approval/ApprovalTargetPreview';
import './ApprovalDetailPage.css';

import { notifyError, reportError } from '../utils/notify';
import { notifyApprovalInboxChanged } from '../utils/approvalInboxBadge';

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  return value.replace('T', ' ').slice(0, 16);
}

/** 저장된 "이름 (역할)" 표기에서 역할 제거 */
function stripRoleSuffix(value?: string | null): string {
  if (!value) return '';
  const text = value.trim();
  if (text.includes(',')) {
    return text
      .split(',')
      .map((part) => stripRoleSuffix(part.trim()))
      .filter(Boolean)
      .join(', ');
  }
  const index = text.indexOf(' (');
  if (index > 0 && text.endsWith(')')) {
    return text.slice(0, index).trim();
  }
  return text;
}

/** 결재 대상자(대기 중이면 대기자, 완료면 최종 승인자) */
function approvalTargetAssignee(data: ApprovalRequest) {
  if (data.status === 'IN_PROGRESS') {
    const pending = (data.steps || [])
      .filter((s) => s.status === 'PENDING')
      .map((s) => stripRoleSuffix(s.assigneeName || ''))
      .filter(Boolean);
    if (pending.length) return pending.join(', ');
    if (data.currentPendingNames) return stripRoleSuffix(data.currentPendingNames);
  }
  const finals = (data.steps || [])
    .filter((s) => s.stepType === 'FINAL')
    .map((s) => stripRoleSuffix(s.assigneeName || ''))
    .filter(Boolean);
  if (finals.length) return finals.join(', ');
  const any = (data.steps || [])
    .map((s) => stripRoleSuffix(s.assigneeName || ''))
    .filter(Boolean);
  return any.length ? Array.from(new Set(any)).join(', ') : '-';
}

function documentSummary(data: ApprovalRequest) {
  const type = approvalTargetLabel(data.targetType);
  const summary = stripRoleSuffix(data.targetInfo?.summary || '');
  if (summary) return `${type} · ${summary}`;
  return `${type} #${data.targetId}`;
}

function ApprovalDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const id = Number(requestId);
  const [data, setData] = useState<ApprovalRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(id)) {
      reportError(setError, '잘못된 결재 요청입니다.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const detail = await fetchApproval(id);
      setData(detail);
      setError('');
    } catch {
      reportError(setError, '결재 상세를 불러오지 못했습니다.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleApprove = async () => {
    if (!data?.myPendingStepId) return;
    try {
      setActing(true);
      const updated = await approveApprovalStep(
        data.requestId,
        data.myPendingStepId,
        comment.trim() || undefined
      );
      setData(updated);
      setComment('');
      setMessage(
        updated.status === 'COMPLETED'
          ? '최종 결재가 완료되었습니다.'
          : '승인 처리되었습니다.'
      );
      notifyApprovalInboxChanged();
    } catch {
      reportError(setError, '승인에 실패했습니다.');
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!data?.myPendingStepId) return;
    try {
      setActing(true);
      const updated = await rejectApprovalStep(
        data.requestId,
        data.myPendingStepId,
        comment.trim() || undefined
      );
      setData(updated);
      setComment('');
      setMessage('반려 처리되었습니다.');
      notifyApprovalInboxChanged();
    } catch {
      reportError(setError, '반려에 실패했습니다.');
    } finally {
      setActing(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!data) return;
    try {
      setPdfDownloading(true);
      const name = data.title?.trim() || String(data.requestId);
      await downloadApprovalPdf(data.requestId, `결재문서_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : 'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.'
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  return (
    <div className="approval-detail-page">
      <header className="approval-detail-page__bar">
        <div>
          <h1>결재 상세</h1>
          <p>요청 내역과 대상 문서를 확인한 뒤 결재하세요.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {data?.status === 'COMPLETED' ? (
            <button
              type="button"
              disabled={pdfDownloading}
              onClick={() => void handleDownloadPdf()}
            >
              {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
            </button>
          ) : null}
          <button type="button" onClick={() => window.close()}>
            창 닫기
          </button>
        </div>
      </header>

      {loading ? <p className="approval-detail-page__hint">불러오는 중...</p> : null}
      {error ? <p className="approval-detail-page__error">{error}</p> : null}
      {message ? <p className="approval-detail-page__ok">{message}</p> : null}

      {!loading && data ? (
        <div className="approval-detail-page__layout">
          <aside className="approval-detail-page__side">
            <div className="approval-detail-page__side-head">
              <h2>{data.title}</h2>
              <span>{approvalStatusLabel(data.status)}</span>
            </div>
            <dl>
              <div>
                <dt>기안자</dt>
                <dd>{stripRoleSuffix(data.requesterName) || '-'}</dd>
              </div>
              <div>
                <dt>대상</dt>
                <dd>{approvalTargetAssignee(data)}</dd>
              </div>
              <div>
                <dt>문서</dt>
                <dd>{documentSummary(data)}</dd>
              </div>
              <div>
                <dt>기안일</dt>
                <dd>{formatDateTime(data.createdAt)}</dd>
              </div>
              {data.status === 'COMPLETED' && data.completedAt ? (
                <div>
                  <dt>승인일</dt>
                  <dd>{formatDateTime(data.completedAt)}</dd>
                </div>
              ) : null}
              {data.status === 'REJECTED' && data.completedAt ? (
                <div>
                  <dt>반려일</dt>
                  <dd>{formatDateTime(data.completedAt)}</dd>
                </div>
              ) : null}
              {data.memo ? (
                <div>
                  <dt>메모</dt>
                  <dd>{data.memo}</dd>
                </div>
              ) : null}
            </dl>

            <h3>결재선</h3>
            <ul className="approval-detail-page__steps">
              {data.steps.map((step: ApprovalStep) => (
                <li key={step.stepId}>
                  <strong>{approvalStepTypeLabel(step.stepType)}</strong>
                  <span>{stripRoleSuffix(step.assigneeName) || step.assigneeId}</span>
                  <em>{approvalStepStatusLabel(step.status)}</em>
                  {step.actedAt ? (
                    <time dateTime={step.actedAt}>{formatDateTime(step.actedAt)}</time>
                  ) : null}
                  {step.comment ? <small>{step.comment}</small> : null}
                </li>
              ))}
            </ul>

            {data.myPendingStepId ? (
              <div className="approval-detail-page__actions">
                <label>
                  <span className="approval-detail-page__actions-title">의견</span>
                  <textarea
                    rows={8}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="결재·반려 의견을 작성하세요 (선택)"
                  />
                </label>
                <div className="approval-detail-page__buttons">
                  <button
                    type="button"
                    className="is-approve"
                    disabled={acting}
                    onClick={() => void handleApprove()}
                  >
                    결재
                  </button>
                  <button
                    type="button"
                    className="is-reject"
                    disabled={acting}
                    onClick={() => void handleReject()}
                  >
                    반려
                  </button>
                </div>
              </div>
            ) : (
              <p className="approval-detail-page__hint">
                {data.status === 'IN_PROGRESS'
                  ? '현재 대기 중인 결재 단계가 아닙니다.'
                  : '처리가 끝난 결재입니다.'}
              </p>
            )}
          </aside>

          <main className="approval-detail-page__main">
            <ApprovalTargetPreview
              targetType={data.targetType}
              targetId={data.targetId}
              targetInfo={data.targetInfo}
            />
          </main>
        </div>
      ) : null}

      {!loading && !data && !error ? (
        <button type="button" onClick={() => navigate('/app')}>
          홈으로
        </button>
      ) : null}
    </div>
  );
}

export default ApprovalDetailPage;
